import * as React from "react";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import * as Sentry from "@sentry/react";
import { ulid } from "ulid";

import { type Evidence } from "@/types/evidences";
import { isQaProgressStep, PendingInterrupt, StreamMessage, WebSocketMessage } from "../chat-ui.types";

const MAX_EVENTS_SIZE = 1000;

interface UseChatWebSocketProps {
  conversationId: string;
  systemId: string;
  excludeFileIds: string[];
  onRefetch: () => void;
}

interface UseChatWebSocketReturn {
  sendMessage: (message: string) => void;
  isReady: boolean;
  isStreaming: boolean;
  messages: StreamMessage[];
  lastSentMessageId: string | null;
  evidences: Evidence[];
  initializeHistory: (messages: StreamMessage[], evidences: Evidence[]) => void;
  allEvents: WebSocketMessage[];
  streamingMessageId: string | null;
  cancelStreaming: () => void;
  pendingInterrupt: PendingInterrupt | null;
  resolveInterrupt: (documentIds: string[]) => void;
  cancelInterrupt: () => void;
  historyTokenWarning: number | null;
  largeFolderWarning: number | null;
  dismissHistoryTokenWarning: () => void;
  dismissLargeFolderWarning: () => void;
}

export const useChatWebSocket = ({
  conversationId,
  systemId,
  excludeFileIds,
  onRefetch,
}: UseChatWebSocketProps): UseChatWebSocketReturn => {
  const [messages, setMessages] = React.useState<StreamMessage[]>([]);
  const [lastSentMessageId, setLastSentMessageId] = React.useState<string | null>(null);
  const [evidences, setEvidences] = React.useState<Evidence[]>([]);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [allEvents, setAllEvents] = React.useState<WebSocketMessage[]>([]);
  const [pendingInterrupt, setPendingInterrupt] = React.useState<PendingInterrupt | null>(null);
  const [historyTokenWarning, setHistoryTokenWarning] = React.useState<number | null>(null);
  const [largeFolderWarning, setLargeFolderWarning] = React.useState<number | null>(null);
  const streamingMessageIdRef = React.useRef<string | null>(null);

  const patchStreamingMessage = React.useCallback((patch: (msg: StreamMessage) => Partial<StreamMessage>) => {
    setMessages(prev => prev.map(msg => (msg.id === streamingMessageIdRef.current ? { ...msg, ...patch(msg) } : msg)));
  }, []);

  const validateEvidenceData = React.useCallback(
    (data: WebSocketMessage): boolean =>
      !!(data.citation_id && data.document_id && data.fse_id && Array.isArray(data.highlights)),
    [],
  );

  const validateWebSocketMessage = React.useCallback((data: WebSocketMessage): boolean => {
    if (!data || typeof data.ev_type !== "string") {
      return false;
    }

    const validEventTypes = [
      "query_started",
      "query_token",
      "query_evidence",
      "query_eot",
      "query_interrupt",
      "query_history_token_warning",
      "query_large_folder_warning",
      "query_answer_source",
      "query_status",
    ];
    return validEventTypes.includes(data.ev_type);
  }, []);

  const processEvent = React.useCallback(
    (data: WebSocketMessage) => {
      if (!validateWebSocketMessage(data)) {
        return;
      }

      setAllEvents(prevEvents => {
        const newEvents = [...prevEvents, data];
        return newEvents.length > MAX_EVENTS_SIZE ? newEvents.slice(-MAX_EVENTS_SIZE) : newEvents;
      });

      try {
        switch (data.ev_type) {
          case "query_started": {
            const newAssistantMessageId = ulid();
            setIsStreaming(true);
            setMessages(prev => [
              ...prev,
              {
                id: newAssistantMessageId,
                content: "",
                isUser: false,
                type: "ai",
                timestamp: new Date(),
              },
            ]);
            streamingMessageIdRef.current = newAssistantMessageId;
            break;
          }
          case "query_token": {
            if (data.t && streamingMessageIdRef.current) {
              patchStreamingMessage(msg => ({ content: msg.content + data.t }));
            }
            break;
          }
          case "query_evidence": {
            if (validateEvidenceData(data) && streamingMessageIdRef.current) {
              setEvidences(prev => [
                ...prev,
                {
                  id: data.citation_id!,
                  citationId: data.citation_id!,
                  documentId: data.document_id!,
                  answerOffsetStart: data.answer_offset_start || 0,
                  answerOffsetEnd: data.answer_offset_end || 0,
                  fseId: data.fse_id!,
                  highlights: [...(data.highlights || [])],
                } as Evidence,
              ]);
            }
            break;
          }
          case "query_eot": {
            setIsStreaming(false);
            streamingMessageIdRef.current = null;
            break;
          }
          case "query_interrupt": {
            if (data.query_id && data.payload) {
              setIsStreaming(false);
              setPendingInterrupt({ queryId: data.query_id, payload: data.payload });
            }
            break;
          }
          case "query_history_token_warning": {
            if (typeof data.tokens === "number") {
              setHistoryTokenWarning(data.tokens);
            }
            break;
          }
          case "query_large_folder_warning": {
            if (typeof data.count === "number") {
              setLargeFolderWarning(data.count);
            }
            break;
          }
          case "query_answer_source": {
            if (data.source === "history" && streamingMessageIdRef.current) {
              patchStreamingMessage(() => ({ answerSource: data.source }));
            }
            break;
          }
          case "query_status": {
            const step = data.step;
            if (isQaProgressStep(step) && streamingMessageIdRef.current) {
              patchStreamingMessage(() => ({ progressStep: step }));
            }
            break;
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [validateEvidenceData, validateWebSocketMessage, patchStreamingMessage],
  );

  const handleWebSocketMessage = React.useCallback(
    (event: MessageEvent) => {
      if (typeof event.data !== "string") {
        return;
      }

      try {
        processEvent(JSON.parse(event.data) as WebSocketMessage);
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [processEvent],
  );

  const { sendJsonMessage, readyState } = useWebSocket<WebSocketMessage>("/pf-mainweb/question-answer/query", {
    shouldReconnect: event => event?.code !== 1000,
    reconnectInterval: 3000,
    skipAssert: true,
    onMessage: handleWebSocketMessage,
    onError: error => {
      Sentry.captureException(error);
    },
  });

  const sendMessage = React.useCallback(
    (message: string) => {
      if (!message?.trim()) {
        return;
      }

      try {
        const newMessage: StreamMessage = {
          id: ulid(),
          content: message,
          isUser: true,
          type: "human",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, newMessage]);
        setLastSentMessageId(newMessage.id);

        if (readyState === ReadyState.OPEN) {
          void onRefetch();
          sendJsonMessage({
            input_type: "query",
            conversation_id: conversationId,
            query: message,
            fse_id: systemId,
            query_id: ulid(),
            exclude_fse_ids: excludeFileIds,
          });
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [readyState, sendJsonMessage, systemId, excludeFileIds, onRefetch, conversationId],
  );

  const cancelStreaming = React.useCallback(() => {
    if (readyState === ReadyState.OPEN && isStreaming) {
      sendJsonMessage({ input_type: "cancel" });
    }
    setIsStreaming(false);
    streamingMessageIdRef.current = null;
  }, [readyState, isStreaming, sendJsonMessage]);

  const resolveInterrupt = React.useCallback(
    (documentIds: string[]) => {
      if (!pendingInterrupt || readyState !== ReadyState.OPEN) {
        return;
      }
      sendJsonMessage({
        input_type: "resume",
        conversation_id: conversationId,
        query_id: pendingInterrupt.queryId,
        value: { document_ids: documentIds },
      });
      // The step from before the pause no longer applies; the resumed graph announces its own.
      patchStreamingMessage(() => ({ progressStep: undefined }));
      setIsStreaming(true);
      setPendingInterrupt(null);
    },
    [pendingInterrupt, readyState, sendJsonMessage, conversationId, patchStreamingMessage],
  );

  const cancelInterrupt = React.useCallback(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({ input_type: "cancel" });
    }
    setPendingInterrupt(null);
    setIsStreaming(false);
    streamingMessageIdRef.current = null;
  }, [readyState, sendJsonMessage]);

  const dismissHistoryTokenWarning = React.useCallback(() => setHistoryTokenWarning(null), []);
  const dismissLargeFolderWarning = React.useCallback(() => setLargeFolderWarning(null), []);

  const isReady = readyState === ReadyState.OPEN;

  const initializeHistory = React.useCallback((msgs: StreamMessage[], evs: Evidence[]) => {
    setMessages(msgs);
    setEvidences(evs);
    // The replaced messages no longer contain a locally sent prompt.
    setLastSentMessageId(null);
  }, []);

  return {
    sendMessage,
    isReady,
    isStreaming,
    messages,
    lastSentMessageId,
    evidences,
    initializeHistory,
    allEvents,
    streamingMessageId: streamingMessageIdRef.current,
    cancelStreaming,
    pendingInterrupt,
    resolveInterrupt,
    cancelInterrupt,
    historyTokenWarning,
    largeFolderWarning,
    dismissHistoryTokenWarning,
    dismissLargeFolderWarning,
  };
};
